import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useToast } from "@/lib/toast";
import { Alert, Button } from "./ui";
import { safeNext } from "./RequireAuth";

/**
 * "Continuer avec Google". The API creates a one-time state and returns Google's
 * URL; the browser then leaves the app. Google sends it back to
 * /api/auth/google/callback, which signs in (httpOnly cookie) and redirects to a
 * fixed page with `?google=…` or `?google_error=…`. No token ever reaches the app.
 */

const NEXT_KEY = "tw_google_next";

/** Full-page navigation to Google (indirection so tests can observe it). */
export const browser = {
  redirect(url: string) {
    window.location.assign(url);
  },
};

export function useGoogleEnabled() {
  return useQuery({
    queryKey: ["google-auth"],
    queryFn: () => api<{ enabled: boolean }>("/auth/google").then((r) => r.enabled),
    staleTime: Infinity,
    retry: false,
  }).data === true;
}

function GoogleLogo() {
  return (
    <svg viewBox="0 0 48 48" className="size-5" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

export function GoogleButton({ intent = "signin", next, label }: { intent?: "signin" | "link"; next?: string; label?: string }) {
  const i18n = useI18n();
  const { t, locale } = i18n;
  const enabled = useGoogleEnabled();
  // Stays true once Google's page is loading so the button cannot be pressed twice.
  const [redirecting, setRedirecting] = useState(false);
  const busy = useRef(false);

  const start = useMutation({
    mutationFn: () => api<{ url: string }>("/auth/google/start", { method: "POST", body: { intent, locale } }),
    onSuccess: ({ url }) => {
      setRedirecting(true);
      try {
        if (next && next !== "/") sessionStorage.setItem(NEXT_KEY, next);
        else sessionStorage.removeItem(NEXT_KEY);
      } catch {
        // Storage unavailable: the customer simply lands on the home page.
      }
      browser.redirect(url);
    },
    onSettled: (_data, error) => {
      if (error) busy.current = false;
    },
  });
  const { reset } = start;

  // Coming back with the browser's Back button restores this page from cache: re-enable the button.
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      busy.current = false;
      setRedirecting(false);
      reset();
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, [reset]);

  if (!enabled) return null;

  return (
    <div className="space-y-3">
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="w-full"
        loading={start.isPending || redirecting}
        aria-busy={start.isPending || redirecting}
        onClick={() => {
          if (busy.current) return;
          busy.current = true;
          start.mutate();
        }}
      >
        {!(start.isPending || redirecting) && <GoogleLogo />}
        {redirecting ? t("auth.googleRedirecting") : (label ?? t("auth.continueWithGoogle"))}
      </Button>
      {start.error && <Alert kind="error">{i18n.errorMessage(start.error)}</Alert>}
    </div>
  );
}

/** "ou" separator between the Google button and the phone form. */
export function OrDivider() {
  const { t } = useI18n();
  const enabled = useGoogleEnabled();
  if (!enabled) return null;
  return (
    <div className="my-5 flex items-center gap-3 text-xs font-semibold uppercase text-subtle">
      <span className="h-px flex-1 bg-line" />
      {t("common.or")}
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

const KNOWN_ERRORS = new Set([
  "GOOGLE_AUTH_UNAVAILABLE",
  "GOOGLE_CANCELLED",
  "GOOGLE_STATE_INVALID",
  "GOOGLE_STATE_EXPIRED",
  "GOOGLE_FAILED",
  "GOOGLE_TOKEN_INVALID",
  "GOOGLE_EMAIL_NOT_VERIFIED",
  "GOOGLE_ACCOUNT_IN_USE",
  "GOOGLE_ALREADY_LINKED",
  "GOOGLE_LINK_SESSION_INVALID",
  "ACCOUNT_DISABLED",
  "RATE_LIMITED",
]);

/**
 * Message for `?google_error=CODE` after a failed Google round trip. The value is
 * only used as a lookup key among known codes, then removed from the URL.
 */
export function useGoogleError(): string | null {
  const { t, tx } = useI18n();
  const [params, setParams] = useSearchParams();
  const raw = params.get("google_error");
  const [code] = useState(() => (raw ? (KNOWN_ERRORS.has(raw) ? raw : "GOOGLE_FAILED") : null));

  useEffect(() => {
    if (!raw) return;
    setParams(
      (p) => {
        p.delete("google_error");
        return p;
      },
      { replace: true },
    );
  }, [raw, setParams]);

  return code ? (tx(`errors.${code}`) ?? t("errors.generic")) : null;
}

/** Shows the outcome of a successful Google round trip (`?google=…`) once, app-wide. */
export function useGoogleReturn() {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const outcome = params.get("google");
    if (!outcome) return;
    params.delete("google");
    const rest = params.toString();
    let target = `${pathname}${rest ? `?${rest}` : ""}`;

    if (outcome === "signed_in" || outcome === "welcome") {
      toast(t(outcome === "welcome" ? "auth.googleWelcome" : "auth.googleSignedIn"));
      try {
        target = safeNext(sessionStorage.getItem(NEXT_KEY), target);
        sessionStorage.removeItem(NEXT_KEY);
      } catch {
        // ignore
      }
    } else if (outcome === "linked") {
      toast(t("auth.googleLinked"));
    }
    navigate(target, { replace: true });
  }, [pathname, search, navigate, toast, t]);
}
